import { useState, useEffect } from 'react';

// Module-level store so state survives a component unmounting and remounting
// within a single page session. Dashboard tabs fully unmount when you switch
// away (Dashboard renders one tab at a time via a switch), which otherwise
// discards their filter/pagination state and forces a fresh fetch on return.
//
// Unlike sessionStorage this keeps native values (Date objects, etc.) with no
// JSON serialization, and it intentionally resets on a full page reload.
const store = new Map();

/**
 * Like useState, but seeded from and mirrored to a per-key module-level store so
 * the value persists across unmount/remount within the page session.
 *
 * @template T
 * @param {string} key - Stable key identifying this piece of state.
 * @param {T | (() => T)} initial - Initial value (or lazy initializer) used only
 *   when nothing is stored for `key` yet.
 * @returns {[T, import('react').Dispatch<import('react').SetStateAction<T>>]}
 */
export function usePersistentState(key, initial) {
  const [state, setState] = useState(() =>
    store.has(key)
      ? store.get(key)
      : typeof initial === 'function'
        ? initial()
        : initial
  );

  useEffect(() => {
    store.set(key, state);
  }, [key, state]);

  return [state, setState];
}
